// @flow
import React, {useEffect, useState, useMemo} from 'react';
import parsePhoneNumber from 'libphonenumber-js';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {Typography, Button, Grid, Hidden} from '@material-ui/core';
import {useForm, FormProvider} from 'react-hook-form';
import {Redirect, useLocation} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import LoadingVeil from '../../../elements/LoadingVeil/LoadingVeil';
import FloatingBottom from '../../configure/common/components/FloatingBottom';
import StoreConfigContainer from '../store-config/containers/StoreConfigContainer';
import AccountConfigContainer from '../account-config/containers/AccountConfigContainer';
import {usePage} from '../../page/utils';
import analytics from '../../../analytics';
import {extractRootDomain} from '../../../utils/string';
import {getCurrencyByCountry} from '../utils';
import HowDoYouFindUs from '../how-do-you-find-us/components/HowDoYouFindUs';
import {transformDomain} from '../utils';
import tiendada from '../../../assets/img/tiendada.png';
import onboarding from '../../../assets/img/onboarding.png';
import backgroundOnboarding from '../../../assets/img/backgroundOnboarding.png';

import type {
  ContactType,
  Country,
  Currency,
  StoreInformation,
  Store,
  StoreType,
  User,
  ResellerInformation,
  Contact,
  MiniStore,
} from '../../../entities';
import type {
  SaveGeneralContactRequest,
  SaveGeneralSettingsRequest,
} from '../../configure/general/api';
import type {SaveAccountRequest} from '../../profile/account/api';
import type {CheckoutAction} from '../../checkout/duck';
import {getBasicDomain} from '../../../utils/component';
// import ProgressBar from './ProgressBar';
// import StoreObjective from './StoreObjective';
import WeeklySalesLevel from './WeeklySalesLevel';
// import ConnectWithMyStore from './ConnectWithMyStore';
// import ContactWithYou from './ContactWithYou';
import {ON_BOARDING_FIELDS} from '../utils/constants';
import {ProgressBar} from '../../../elements/ProgressBar/ProgressBar';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import {CheckCircle} from '@material-ui/icons';

import type {SetInitialConfigRequest} from '../duck';
type Props = {|
  user?: User,
  initialAuthDone?: boolean,
  isSaving?: boolean,
  currency?: Currency,
  isFetchingCurrency?: boolean,
  currencies?: Currency[],
  isFetchingCurrencies?: boolean,
  country?: Country,
  countries?: Country[],
  isFetchingCountries?: boolean,
  store?: Store,
  isFetchingContact?: boolean,
  contactTypes?: ContactType[],
  information?: StoreInformation,
  isFetchingInformation?: boolean,
  setInitialConfig: ({domain: string}) => void,
  validateDomain: ({subdomain: string}) => void,
  getStoreTypes: () => void,
  validationResult?: boolean,
  storeTypes?: StoreType[],
  isGettingStoreTypes?: boolean,
  isValidating?: boolean,
  isSettingConfig?: boolean,
  resellerInformation: ResellerInformation,
  isFetchingResellerInformation: boolean,
  isSavingCanalInf?: boolean,
  saveCanalInfSuccess?: boolean,
  saveCanalInf: ({
    isInfluencer: boolean,
    channel: string,
    influencer: string,
  }) => void,
  saveGeneralSettings: ({dataGeneral: SaveGeneralSettingsRequest}) => void,
  saveAccountSettings: ({data: SaveAccountRequest}) => void,
  saveOnboardingInf: ({data: any}) => void,
  isSavingOnboardingInf?: boolean,
  saveOnboardingInfSuccess?: boolean,
  errorOnboarding?: string,
  error?: string,
  contact?: Contact[],
  initialConfigSaved?: MiniStore,
  freeTrialSubscribe: (payload: CheckoutAction) => void,
|};

const useStyles = makeStyles(theme => ({
  mainWrap: {
    padding: '1rem',
  },
  logo: {
    height: 48,
    width: 'auto',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    textAlign: 'center',
  },
  progressWrap: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  mobileProgressBar: {
    width: '100%',
  },
  progress: {
    width: '100%',
    marginRight: '8px',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  onBoardingHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  progressWrapHeader: {
    width: '50%',
  },
  form: {
    width: '100%',
    backgroundColor: '#E7F1FD',
    boxSizing: 'border-box',
    padding: theme.spacing(4),
    textAlign: 'center',
    borderRadius: theme.borderRadius,
    maxWidth: 440,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 328,
    },
  },
  wrapperContainer: {
    background: 'white',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      paddingBottom: '200px',
    },
  },
  inputForm: {
    marginBottom: theme.spacing(3),
  },
  innerInputForm: {
    background: 'white',
    textAlign: 'start',
  },
  bigTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 600,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'unset',
    },
  },
  text: {
    maxWidth: 440,
    marginBottom: theme.spacing(4),
  },
  submit: {
    margin: theme.spacing(1, 0, 2),
    fontWeight: 600,
  },
  errorText: {
    marginBottom: theme.spacing(2),
  },
  loading: {
    position: 'absolute',
  },
  buttonWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '.5rem',
  },
  divider: {
    border: '1px solid #E8E8ED',
    margin: '16px auto',
  },
  leftSide: {
    maxWidth: '700px',
    padding: '0 .5rem',
    margin: 'auto',
  },
  imageSlider: {
    float: 'left',
    height: 'auto',
    padding: '16px',
    paddingBottom: 0,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: 'auto',
      margin: 'auto',
      padding: '32px',
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  backgroundRightSide: {
    overflow: 'hidden',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    maxHeight: '300px',
    [theme.breakpoints.up('sm')]: {
      maxHeight: '500px',
    },
    [theme.breakpoints.up('md')]: {
      minHeight: '100vh',
      height: '100%',
      maxHeight: '100%',
    },
    backgroundImage: `url(${backgroundOnboarding})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },

  fieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    width: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      paddingTop: 0,
    },
  },
  fieldSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  circle: {
    cursor: 'pointer',
    width: '10px',
    height: '10px',
    background: '#D4D4DD',
    borderRadius: '50%',
    margin: '6px',
  },
  circleActive: {
    background: '#717190',
  },
  stepsCircleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 'auto',
    marginTop: '32px',
  },
  subtitleB: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: '24px',
    color: 'white',
    textAlign: 'center',
    marginTop: '16px',
  },
  titleB: {
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '44px',
    lineHeight: '58px',
    color: 'white',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      marginTop: '48px',
    },
  },
  stickyImgWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default usePage<Props>(OnBoarding, 'On Boarding');

function OnBoarding(props: Props) {
  const {
    user,
    store,
    initialAuthDone,
    isFetchingContact,
    contactTypes,
    isFetchingCurrency,
    currencies,
    isFetchingCurrencies,
    isFetchingInformation,
    countries,
    isFetchingCountries,
    isValidating,
    // setInitialConfig,
    isSettingConfig,
    getStoreTypes,
    // saveGeneralSettings,
    // saveCanalInf,
    // saveAccountSettings,
    validateDomain,
    saveOnboardingInf,
    isSavingOnboardingInf,
    // saveOnboardingInfSuccess,
    // currency,
    // country,
    errorOnboarding,
    validationResult,
    initialConfigSaved,
    error,
    storeTypes = [],
    // freeTrialSubscribe,
  } = props;
  const classes = useStyles();

  const formMethods = useForm({
    mode: 'onChange',
  });

  const {
    register,
    errors,
    control,
    trigger,
    setValue,
    watch,
    getValues,
  } = formMethods;

  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  // const history = useHistory();

  const [step, setStep] = useState(0);
  const [storeTypeSelected, setStoreTypeSelected] = useState();

  const rootDomainDataSource = useMemo(
    () =>
      store && store.domains && store.domains.length > 0
        ? `.${extractRootDomain(getBasicDomain(store.domains))}`
        : '',
    [store]
  );

  const data = watch();

  const percentage = 60;

  const rateCompleted = `${percentage}%`;

  const storeName = watch('name');

  const subdomain = useMemo(() => {
    if (storeName) return transformDomain(storeName);
    return '';
  }, [storeName]);

  useEffect(() => {
    if (subdomain) {
      validateDomain({subdomain});
    }
  }, [subdomain, validateDomain]);

  //Redirect to second step
  /*   useEffect(() => {
    if (!isSavingOnboardingInf) {
      if (
        store &&
        store.name &&
        !!store.storeType &&
        user &&
        user.firstName &&
        user.lastName &&

      ) {
        setStep(1);
      }
    }
  }, [isSavingOnboardingInf, store, user]); */

  const [onBoardingRequest, setOnBoardingRequest] = useState({});
  const [messageError, setMessageError] = useState('');
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const storeUrl = useMemo(() => {
    if (
      initialConfigSaved &&
      initialConfigSaved.storeDomain &&
      initialConfigSaved.token
    ) {
      let qp = {...queryParams};
      qp.s_u_token = initialConfigSaved.token;
      return `https://${
        initialConfigSaved.storeDomain
      }/admin/home?${queryString.stringify(qp)}`;
    }
    return '';
  }, [initialConfigSaved, queryParams]);

  const handleClickContinue = () => {
    if (store && !isValidating)
      trigger().then(valid => {
        const errorsValues = Object.values(errors);

        const isValid = errorsValues.length === 1 && step === 0 ? true : valid;

        if (!validationResult)
          setMessageError(
            'Por favor utlizar otro nombre de tienda para continuar'
          );
        else setMessageError('');

        if (!(validationResult && isValid && rootDomainDataSource)) {
          return;
        }

        if (
          errorsValues.length < 1 ||
          (errorsValues.length === 1 && step === 0)
        ) {
          const generalSettingsRequest = {
            name: data.name
              ? !store || data.name !== store.name
                ? data.name
                : undefined
              : undefined,
            storeTypeId: storeTypeSelected,
            storeBusinessModel: data[ON_BOARDING_FIELDS.STORE_BUSINESS_TYPE],
          };
          // saveGeneralSettings({dataGeneral: generalSettingsRequest});
          const accountSettingsRequest = {
            firstName:
              user && data.firstName !== user.firstName
                ? data.firstName
                : undefined,
            lastName:
              user && data.lastName !== user.lastName
                ? data.lastName
                : undefined,
          };
          // saveAccountSettings({
          //   data: {firstName: data.firstName, lastName: data.lastName},
          // });

          const dataContactRequest: SaveGeneralContactRequest[] = [];
          if (contactTypes) {
            let ct = contactTypes.find(ct => ct.name === 'CORPORATE_EMAIL');

            if (ct && user) {
              dataContactRequest.push({
                contactTypeId: ct.id,
                data: user.email,
              });
            }

            ct = contactTypes.find(ct => ct.name === 'WHATS_APP_NUMBER');
            const whatsappDefault = user && user.phone;

            if (ct && whatsappDefault) {
              dataContactRequest.push({
                contactTypeId: ct.id,
                data: whatsappDefault,
              });
            }

            const ctPhone = contactTypes.find(ct => ct.name === 'PHONE_NUMBER');
            if (ctPhone && whatsappDefault) {
              dataContactRequest.push({
                contactTypeId: ctPhone.id,
                data: whatsappDefault,
              });
            }
          }

          let country: ?Country = undefined;
          if (countries && countries.length > 0 && user && user.phone) {
            const phoneNumber = parsePhoneNumber(user.phone);

            if (!phoneNumber) {
              country = countries.find(
                c => c.phonePrefix && user.phone.startsWith(c.phonePrefix)
              );
            } else {
              country = countries.find(c => c.code === phoneNumber.country);
            }
          }
          const countryRequest = {
            countryId: country ? country.id : undefined,
          };
          // saveStoreCountry({
          //   countryId: defaultCountry.id,
          // });

          let currency = undefined;
          if (currencies && currencies.length > 0 && country) {
            currency = currencies.find(
              c => country && getCurrencyByCountry(country.code) === c.code
            );
          }
          const currencyRequest = {
            currencyId: currency ? currency.id : undefined,
          };

          setOnBoardingRequest({
            generalSettingsRequest,
            accountSettingsRequest,
            dataContactRequest,
            countryRequest,
            currencyRequest,
          });
          // if (step === 0) setStep(s => s + 1);

          // if (step === 1) {
          const canalInfRequest = {
            // isInfluencer: data.isInfluencer === 'si',
            // influencer: data.influencerName ? data.influencerName : undefined,
            channel: data[ON_BOARDING_FIELDS.REFERRAL_INFORMATION],
          };
          setOnBoardingRequest(r => ({
            ...r,
            canalInfRequest,
          }));

          const goalInfRequest = {
            storeGoal: data[ON_BOARDING_FIELDS.STORE_GOAL],
            salesFrequency: data[ON_BOARDING_FIELDS.WEEKLY_SALES],
            storeInterests: data[ON_BOARDING_FIELDS.STORE_INTEREST],
          };

          const supportInfRequest = {
            getAssistance: data[ON_BOARDING_FIELDS.SUPPORT_INFORMATION],
          };

          const subdom =
            subdomain ||
            (store && store.name.replace(/\s+/g, '').toLowerCase()) ||
            '';

          const storeType = storeTypes.find(
            st => st.id === generalSettingsRequest.storeTypeId
          );

          const onboardingData = {
            ...onBoardingRequest,
            generalSettingsRequest,
            accountSettingsRequest,
            dataContactRequest,
            countryRequest,
            currencyRequest,
            canalInfRequest,
            subdomain: subdom,
            goalInfRequest,
            supportInfRequest,
          };

          const initialConfigRequest: SetInitialConfigRequest = {
            store: generalSettingsRequest.name,
            storeType: storeType ? storeType.typeName : undefined,
            firstName: accountSettingsRequest.firstName,
            lastName: accountSettingsRequest.lastName,
            email: user ? user.email : undefined,
            phone: user ? user.phone : undefined,
            whatsappPhone: user ? user.phone : undefined,
            country: country ? country.code : undefined,
            currency: currency ? currency.code : undefined,
            referralChannel: canalInfRequest.channel,
            subdomainName: onboardingData.subdomain,
            storeGoal: goalInfRequest.storeGoal,
            salesFrequency: goalInfRequest.salesFrequency,
            storeInterests: goalInfRequest.storeInterests,
            getAssistant: supportInfRequest.getAssistance,
          };

          console.log({
            initialConfigRequest,
            onboardingData,
          });

          saveOnboardingInf({
            data: onboardingData,
            initialConfigRequest,
          });
          // saveCanalInf(canalInfRequest);
          // }
        }
      });
  };

  useEffect(() => {
    getStoreTypes();
  }, [getStoreTypes]);

  useEffect(() => {
    if (user) {
      analytics.fireCustomEvent('Onboarding Started', {
        category: 'Onboarding',
        action: 'Started',
        user_type: user.type,
      });
    }
  }, [user]);

  // When initial config is changed, we redirect
  useEffect(() => {
    if (
      initialConfigSaved &&
      initialConfigSaved.storeDomain &&
      initialConfigSaved.token
    ) {
      setShowRedirectModal(true);

      analytics.fireCustomEvent('Onboarding Completed', undefined, [
        'user-guiding',
      ]);
    }
  }, [initialConfigSaved]);

  const handleAutoRedirect = React.useCallback(() => {
    if (storeUrl && window) {
      window.location.replace(storeUrl);
    }
  }, [storeUrl]);

  useEffect(() => {
    let timer;
    if (showRedirectModal && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (showRedirectModal && countdown === 0) {
      handleAutoRedirect();
    }
    return () => clearTimeout(timer);
  }, [showRedirectModal, countdown, handleAutoRedirect]);

  const buildTitleForm = (label: string) => (
    <Typography component="span" variant="h6" className={classes.sectionTitle}>
      {label}
    </Typography>
  );

  if (isFetchingCountries || isFetchingCurrencies) return null;
  if (!countries || !currencies)
    return <>Ocurrió un error =(, por favor refresca la página</>;
  if (!!store && store.initialConfig) return <Redirect to={'/'} />;

  return (
    <FormProvider {...formMethods}>
      <Grid container className={classes.wrapperContainer}>
        <Grid item container sm={12} md={7} className={classes.leftSide}>
          <Hidden smDown>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <header className={classes.onBoardingHeader}>
                <div style={{width: 'fit-content', marginTop: '32px'}}>
                  <img alt="tiendada" src={tiendada} />
                </div>

                <Hidden smDown>
                  <div className={classes.progressWrapHeader}>
                    <ProgressBar
                      value={percentage}
                      rateCompleted={rateCompleted}
                    />
                  </div>
                </Hidden>
              </header>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <hr className={classes.divider} />
            </Grid>
          </Hidden>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <div className={classes.fieldsContainer}>
              <div
                className={classes.fieldSection}
                style={{display: step === 0 ? 'flex' : 'none'}}
              >
                {buildTitleForm('Datos del creador')}
                {user && (
                  <AccountConfigContainer
                    register={register}
                    errors={errors}
                    control={control}
                  />
                )}
              </div>
              <div
                className={classes.fieldSection}
                style={{display: step === 0 ? 'flex' : 'none'}}
              >
                {buildTitleForm('Datos de tu tienda')}
                {store &&
                  contactTypes &&
                  currencies &&
                  !isFetchingContact &&
                  !isFetchingInformation &&
                  !isFetchingCurrency && (
                    <StoreConfigContainer
                      subdomainRegister={register}
                      errors={errors}
                      control={control}
                      trigger={trigger}
                      setValue={setValue}
                      getValues={getValues}
                      setStoreTypeSelected={setStoreTypeSelected}
                    />
                  )}
              </div>
              {/*   <div
                className={classes.fieldSection}
                style={{display: step === 0 ? 'flex' : 'none'}}
              >
                {buildTitleForm('Objetivo de la tienda')}
                {store &&
                  contactTypes &&
                  currencies &&
                  !isFetchingContact &&
                  !isFetchingInformation &&
                  !isFetchingCurrency && <StoreObjective />}
              </div>{' '} */}
              <div
                className={classes.fieldSection}
                style={{display: step === 0 ? 'flex' : 'none'}}
              >
                {buildTitleForm('Nivel de ventas semanal')}
                {store &&
                  contactTypes &&
                  currencies &&
                  !isFetchingContact &&
                  !isFetchingInformation &&
                  !isFetchingCurrency && <WeeklySalesLevel />}
              </div>
              {/* <div
                className={classes.fieldSection}
                style={{display: step === 1 ? 'flex' : 'none'}}
              >
                {buildTitleForm('Me interesa conectar con mi TiendaDa:')}
                <ConnectWithMyStore />
              </div> */}
              {/* <div
                className={classes.fieldSection}
                style={{display: step === 1 ? 'flex' : 'none'}}
              >
                {buildTitleForm(
                  '¿Deseas que un asesor se contacte contigo para mostrarte como potenciar tus ventas con TiendaDa?'
                )}
                <ContactWithYou />
              </div> */}
              <div
                className={classes.fieldSection}
                style={{display: step === 0 ? 'flex' : 'none'}}
              >
                {buildTitleForm('¿Cómo supiste de nosotros?')}
                <HowDoYouFindUs />
              </div>

              {/* {step === 1 && <OnboardingTrialCard />} */}
            </div>
            <Hidden smDown>
              <div className={classes.buttonWrap}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleClickContinue}
                  style={{
                    textTransform: 'none',
                    marginTop: '32px',
                    padding: '12px',
                    borderRadius: '40px',
                    maxWidth: '320px',
                    // pointerEvents: store && !isValidating ? 'default' : 'none',
                  }}
                >
                  {/* {step === 0 ? 'Continuar' : 'Completar Tienda'} */}
                  Continuar
                </Button>
              </div>
              <Hidden smDown>
                <br />
                {step === 1 && (
                  <div style={{margin: 'auto', width: 'fit-content'}}>
                    <Button
                      color="primary"
                      onClick={() => {
                        if (store && !isValidating) setStep(p => p - 1);
                      }}
                      style={{
                        textTransform: 'none',
                        borderRadius: '40px',
                        // pointerEvents:
                        //   store && !isValidating ? 'default' : 'none',
                      }}
                    >
                      Regresar
                    </Button>
                  </div>
                )}
              </Hidden>
              {/* <div className={classes.stepsCircleWrapper}>
                {[...Array(2).keys()].map((i: number) => (
                  <div
                    key={i}
                    className={`${classes.circle} ${
                      step === i ? classes.circleActive : ''
                    }`}
                    onClick={() => setStep(i)}
                  />
                ))}
              </div> */}
            </Hidden>
            {/* <Hidden mdUp>
              <br />
              {step === 1 && (
                <div style={{margin: 'auto', width: 'fit-content'}}>
                  <Button
                    color="primary"
                    onClick={() => {
                      if (store && !isValidating) setStep(p => p - 1);
                    }}
                    style={{
                      textTransform: 'none',
                      borderRadius: '40px',
                      // pointerEvents:
                      //   store && !isValidating ? 'default' : 'none',
                    }}
                  >
                    Regresar
                  </Button>
                </div>
              )}
            </Hidden> */}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={5} style={{backgroundColor: '#6425E4'}}>
          <div className={classes.backgroundRightSide}>
            <Hidden smDown>
              <div style={{padding: '16px'}}>
                <div className={classes.titleB}>¡Empieza a vender!</div>
                {/* {step === 0 ? (
                  <>
                    <div className={classes.titleB}>¡Empieza a vender!</div>
                    <div className={classes.subtitleB}>
                      A un paso de iniciar una nueva etapa.
                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.titleB}>
                      ¡Tu tienda ya casi esta lista!
                    </div>
                    <div className={classes.subtitleB}>
                      Y luego, a compartirla.
                    </div>
                  </>
                )} */}
              </div>
            </Hidden>
            <Hidden smDown>
              <div className={classes.stickyImgWrap}>
                <div
                  style={{
                    height: 'fit-content',
                    position: 'fixed',
                    bottom: '0',
                  }}
                >
                  <img
                    alt="demo"
                    src={onboarding}
                    className={classes.imageSlider}
                  />
                </div>
              </div>
            </Hidden>
          </div>
        </Grid>
      </Grid>
      <LoadingVeil visible={isSavingOnboardingInf || isSettingConfig} />
      <FloatingBottom>
        <div className={classes.mainWrap}>
          {initialAuthDone && !user && <Redirect to={'/login'} />}
          <div className={classes.progressWrap}>
            <ProgressBar
              value={percentage}
              rateCompleted={rateCompleted}
              className={classes.mobileProgressBar}
            />
          </div>
          <div className={classes.buttonWrap}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleClickContinue}
              style={{
                textTransform: 'none',
                borderRadius: '40px',
                // pointerEvents: store && !isValidating ? 'default' : 'none',
              }}
            >
              {/* {step < 1 ? 'Continuar' : 'Completar Tienda'} */}
              Continuar
            </Button>
          </div>
        </div>
      </FloatingBottom>
      <SnackBar mode="error" message={error ? error : ''} />
      <SnackBar
        mode="error"
        message={messageError}
        handleClose={() => setMessageError('')}
      />

      <SnackBar mode="error" message={errorOnboarding ? errorOnboarding : ''} />

      <CustomModal open={showRedirectModal} handleCloseModal={() => {}}>
        <div
          style={{textAlign: 'center', padding: '24px 16px', maxWidth: '420px'}}
        >
          <CheckCircle
            style={{fontSize: 64, color: '#00B382', marginBottom: '16px'}}
          />
          <Typography
            variant="h5"
            style={{fontWeight: 600, marginBottom: '16px'}}
          >
            Tu tienda fue creada con éxito
          </Typography>
          <Typography variant="body1" style={{marginBottom: '16px'}}>
            Redirigiendo en {countdown} segundos o puedes ir ahora:
          </Typography>
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              backgroundColor: '#6425E4',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '40px',
              textDecoration: 'none',
              fontWeight: 600,
              marginBottom: '16px',
            }}
          >
            Ir a mi tienda
          </a>
          <div
            style={{
              backgroundColor: '#f5f5f5',
              padding: '12px',
              borderRadius: '8px',
              wordBreak: 'break-all',
              fontSize: '12px',
              color: '#666',
            }}
          >
            {storeUrl}
          </div>
          <Typography
            variant="caption"
            style={{
              display: 'block',
              marginTop: '12px',
              color: '#888',
              fontSize: '11px',
            }}
          >
            Si no se redirige automaticamente, haz clic en el boton o copia y
            pega la URL
          </Typography>
        </div>
      </CustomModal>
    </FormProvider>
  );
}

OnBoarding.propTypes = {
  user: PropTypes.object,
  initialAuthDone: PropTypes.bool,
  isSaving: PropTypes.bool,
  currency: PropTypes.object,
  isFetchingCurrency: PropTypes.bool,
  currencies: PropTypes.array,
  isFetchingCurrencies: PropTypes.bool,
  country: PropTypes.object,
  countries: PropTypes.array,
  isFetchingCountries: PropTypes.bool,
  store: PropTypes.object,
  isFetchingContact: PropTypes.bool,
  contactTypes: PropTypes.array,
  information: PropTypes.object,
  isFetchingInformation: PropTypes.bool,
  setInitialConfig: PropTypes.func.isRequired,
  validateDomain: PropTypes.func.isRequired,
  validationResult: PropTypes.bool,
  isValidating: PropTypes.bool,
  isSettingConfig: PropTypes.bool,
  resellerInformation: PropTypes.object,
  isSavingCanalInf: PropTypes.bool,
  isFetchingResellerInformation: PropTypes.bool,
  saveCanalInf: PropTypes.func,
  saveGeneralSettings: PropTypes.func.isRequired,
  saveAccountSettings: PropTypes.func.isRequired,
  saveCanalInfSuccess: PropTypes.bool,
  saveOnboardingInf: PropTypes.func.isRequired,
  isSavingOnboardingInf: PropTypes.bool,
  saveOnboardingInfSuccess: PropTypes.bool,
  errorOnboarding: PropTypes.string,
  initialConfigSaved: PropTypes.object,
  error: PropTypes.string,
  contact: PropTypes.array,
  freeTrialSubscribe: PropTypes.func.isRequired,
};
